import type { ClientDocument } from "@/features/documents/types";
import type {
  Addressee,
  Letter,
  LetterStatus,
  LetterTemplate,
  MergeField,
} from "@/features/letters/types";
import { apiFetch, apiFetchBlob } from "@/lib/api";

interface Envelope<T> {
  success: boolean;
  message: string;
  data: T;
}

interface ApiTemplate {
  id: string;
  name: string;
  recipient: string;
  subject: string;
  body: string;
  is_system: boolean;
  is_hidden: boolean;
  is_active: boolean;
}

interface ApiLetter {
  id: string;
  client: string;
  client_name: string;
  case: string | null;
  template: string | null;
  template_name: string;
  subject: string;
  body_html: string;
  status: LetterStatus;
  status_display: string;
  addressee: Addressee;
  addressee_display: string;
  merge_fields: Record<string, unknown>;
  document: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
}

function toTemplate(a: ApiTemplate): LetterTemplate {
  return {
    id: a.id,
    name: a.name,
    recipient: a.recipient,
    subject: a.subject,
    body: a.body,
    isSystem: a.is_system,
    isHidden: a.is_hidden ?? false,
    isActive: a.is_active,
  };
}

function toDocument(raw: Record<string, unknown> | null): ClientDocument | null {
  if (!raw) return null;
  const d = raw as Record<string, string | number | null>;
  return {
    id: String(d.id),
    client: String(d.client),
    clientName: String(d.client_name ?? ""),
    case: (d.case as string | null) ?? null,
    folder: (d.folder as string | null) ?? null,
    name: String(d.name ?? ""),
    source: d.source as ClientDocument["source"],
    sourceDisplay: String(d.source_display ?? ""),
    status: d.status as ClientDocument["status"],
    statusDisplay: String(d.status_display ?? ""),
    formNumber: String(d.form_number ?? ""),
    version: Number(d.version ?? 1),
    versionGroup: String(d.version_group ?? ""),
    contentType: String(d.content_type ?? ""),
    sizeBytes: Number(d.size_bytes ?? 0),
    downloadPath: String(d.download_path ?? ""),
    isDeleted: Boolean(d.is_deleted ?? false),
    deletedAt: (d.deleted_at as string | null) ?? null,
    uploadedBy: (d.uploaded_by as string | null) ?? null,
    uploadedByName: (d.uploaded_by_name as string | null) ?? null,
    isPublished: Boolean(d.is_published ?? false),
    proformaStatus: (d.proforma_status as string | null) ?? null,
    proformaStatusDisplay: (d.proforma_status_display as string | null) ?? null,
    createdAt: String(d.created_at ?? ""),
    updatedAt: String(d.updated_at ?? ""),
  };
}

function toLetter(a: ApiLetter): Letter {
  return {
    id: a.id,
    client: a.client,
    clientName: a.client_name,
    case: a.case,
    template: a.template,
    templateName: a.template_name,
    subject: a.subject,
    bodyHtml: a.body_html,
    status: a.status,
    statusDisplay: a.status_display,
    addressee: a.addressee,
    addresseeDisplay: a.addressee_display,
    mergeFields: a.merge_fields ?? {},
    document: toDocument(a.document),
    createdAt: a.created_at,
    updatedAt: a.updated_at,
  };
}

/** GET /letters/templates/ — firm + shared system templates. Pass
 * `includeHidden` for the Settings library (surfaces hidden system rows). */
export async function listLetterTemplates(
  includeHidden = false,
): Promise<LetterTemplate[]> {
  const suffix = includeHidden ? "&include_hidden=true" : "";
  const body = await apiFetch<Envelope<ApiTemplate[]>>(
    `/letters/templates/?page_size=200${suffix}`,
  );
  return body.data.map(toTemplate);
}

export const LETTER_RECIPIENT_OPTIONS: { value: string; label: string }[] = [
  { value: "CLIENT", label: "Client" },
  { value: "IRS", label: "IRS" },
  { value: "OTHER", label: "Other" },
];

export interface LetterTemplatePayload {
  name: string;
  recipient: string;
  subject: string;
  body: string;
}

/** POST /letters/templates/ — create a firm template. */
export async function createLetterTemplate(
  payload: LetterTemplatePayload,
): Promise<LetterTemplate> {
  const body = await apiFetch<Envelope<ApiTemplate>>("/letters/templates/", {
    method: "POST",
    json: payload,
  });
  return toTemplate(body.data);
}

/** PATCH /letters/templates/:id/ — update a firm template. */
export async function updateLetterTemplate(
  id: string,
  payload: LetterTemplatePayload,
): Promise<LetterTemplate> {
  const body = await apiFetch<Envelope<ApiTemplate>>(`/letters/templates/${id}/`, {
    method: "PATCH",
    json: payload,
  });
  return toTemplate(body.data);
}

/** DELETE /letters/templates/:id/ — remove a firm template. */
export function deleteLetterTemplate(id: string): Promise<void> {
  return apiFetch<void>(`/letters/templates/${id}/`, { method: "DELETE" });
}

/** GET /letters/?client= */
export async function listClientLetters(clientId: string): Promise<Letter[]> {
  const body = await apiFetch<Envelope<ApiLetter[]>>(
    `/letters/?page_size=100&client=${clientId}`,
  );
  return body.data.map(toLetter);
}

/** POST /letters/ — start a draft letter from a template. */
export async function createLetter(
  clientId: string,
  templateId: string,
  addressee: Addressee = "T",
): Promise<Letter> {
  const body = await apiFetch<Envelope<ApiLetter>>("/letters/", {
    method: "POST",
    json: { client: clientId, template: templateId, addressee },
  });
  return toLetter(body.data);
}

export interface GenerateArgs {
  fields?: Record<string, unknown>;
  addressee?: Addressee;
  /** Optional Save-to-Documents name (ASCII only; re-validated server-side). */
  documentName?: string;
}

/** POST /letters/:id/generate/ — merge + render the letterhead PDF, mark FINAL. */
export async function generateLetter(id: string, args: GenerateArgs = {}): Promise<Letter> {
  const json: Record<string, unknown> = {};
  if (args.fields) json.fields = args.fields;
  if (args.addressee) json.addressee = args.addressee;
  if (args.documentName) json.document_name = args.documentName;
  const body = await apiFetch<Envelope<ApiLetter>>(`/letters/${id}/generate/`, {
    method: "POST",
    json,
  });
  return toLetter(body.data);
}

/** Create a letter from a template and immediately generate its PDF (no fill-in). */
export async function createAndGenerateLetter(
  clientId: string,
  templateId: string,
  args: GenerateArgs = {},
): Promise<Letter> {
  const letter = await createLetter(clientId, templateId, args.addressee);
  return generateLetter(letter.id, args);
}

/** POST /letters/:id/preview/ — merged letterhead PDF as a Blob, nothing saved. */
export function previewLetter(id: string, args: GenerateArgs = {}): Promise<Blob> {
  return apiFetchBlob(`/letters/${id}/preview/`, { method: "POST", json: args });
}

interface ApiMergeFields {
  fields: MergeField[];
  system: Record<string, string>;
}

/** GET /letters/templates/:id/merge-fields/ — fill-in fields + system token values. */
export async function resolveMergeFields(
  templateId: string,
  clientId: string,
  addressee: Addressee = "T",
): Promise<ApiMergeFields> {
  const body = await apiFetch<Envelope<ApiMergeFields>>(
    `/letters/templates/${templateId}/merge-fields/?client=${clientId}&addressee=${addressee}`,
  );
  return { fields: body.data.fields ?? [], system: body.data.system ?? {} };
}

/** POST /letters/templates/:id/clone/ — copy a template into the firm library. */
export async function cloneTemplate(id: string): Promise<LetterTemplate> {
  const body = await apiFetch<Envelope<ApiTemplate>>(`/letters/templates/${id}/clone/`, {
    method: "POST",
  });
  return toTemplate(body.data);
}

/** POST /letters/templates/:id/hide/ — suppress a system template for this firm. */
export function hideTemplate(id: string): Promise<void> {
  return apiFetch<void>(`/letters/templates/${id}/hide/`, { method: "POST" });
}

/** POST /letters/templates/:id/restore/ — un-hide a system template. */
export function restoreTemplate(id: string): Promise<void> {
  return apiFetch<void>(`/letters/templates/${id}/restore/`, { method: "POST" });
}

export function deleteLetter(id: string): Promise<void> {
  return apiFetch<void>(`/letters/${id}/`, { method: "DELETE" });
}
